import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import type {
  ClearanceDetail,
  ClearanceNote,
  PaginatedClearanceNoteList,
  PatchedClearanceNote,
  PatchedClearanceStatusUpdate,
} from '../../../../../js/api';
import { ConnectionsService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { DocumentsTab } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import type { CodeDefinitionOption } from '../../../components/dropdown-select';
import { DropdownSelect } from '../../../components/dropdown-select';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { WorkflowScopes } from '../../../constants/workflows';
import { useActingAsEntity } from '../../../hooks/useActingAsEntity';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useStaffOrHasPermission } from '../../../hooks/useStaffOrHasPermission';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type { ActivityHistory, ActivityStatusHistory } from '../../../utils/activity-types';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { ClearanceDetailsTab } from './ClearanceDetailsTab';
import { ClearanceQuickDetails } from './ClearanceQuickDetails';
import { ConnectionsTab } from './ConnectionsTab';
import {
  CLEARANCE_CHANGE_HISTORY_QUERY_KEY,
  CLEARANCE_NOTES_QUERY_KEY,
  CLEARANCE_QUERY_KEY,
  CLEARANCE_STATUS_HISTORY_QUERY_KEY,
  ORGANIZATION_CLEARANCE_LIST_QUERY_KEY,
} from './queryKeys';

const WORKFLOWS_META_QUERY_KEY = 'connections.clearance';

type ClearanceDrawerProps = {
  clearanceId: string;
  onClose?: () => void;
  prevId?: string | null;
  nextId?: string | null;
  selectedQueueCode?: string | undefined;
  listQueryKeyBase?: string;
};

export function ClearanceDrawer({
  clearanceId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
  listQueryKeyBase,
}: ClearanceDrawerProps) {
  const queryClient = useQueryClient();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] = useState<string | null>(null);
  const canShowStatusTransitions = useStaffOrHasPermission('update__connections__clearance');
  const isMobile = useIsMobile();
  const { isStaff } = useActingAsEntity();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(
    () => [
      { code: 'details', label: 'Details' as const },
      { code: 'connections', label: 'Connections' as const },
      { code: 'documents', label: 'Documents' as const },
    ],
    []
  );
  const [activeTab, setActiveTab] = useState<string>('details');
  const hasSetInitialTab = useRef(false);

  // Reset initial tab flag whenever the drawer opens for a new clearance
  useEffect(() => {
    hasSetInitialTab.current = false;
    setActiveTab('details');
  }, [clearanceId]);

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLDivElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      recheckOn: { id: clearanceId, tabs },
    });

  const {
    data: clearanceData,
    isLoading: isLoadingClearance,
    error: clearanceError,
  } = useQuery<ClearanceDetail>({
    queryKey: [CLEARANCE_QUERY_KEY, clearanceId],
    queryFn: () => ConnectionsService.connectionsClearanceRetrieve({ id: clearanceId }),
    enabled: !!clearanceId,
  });

  const { statuses: workflowStatuses } = useWorkflowMetadata(WORKFLOWS_META_QUERY_KEY);

  // Default to connections tab if there are open connections, otherwise details
  useEffect(() => {
    if (!hasSetInitialTab.current && clearanceData) {
      hasSetInitialTab.current = true;
      if ((clearanceData.related_connections ?? []).length > 0) {
        setActiveTab('connections');
      }
    }
  }, [clearanceData]);

  // Helper: after an action refreshes, if this item moved out of the current queue, auto-advance
  const checkAndNavigateIfRemoved = (updated?: ClearanceDetail) => {
    // Only auto-advance when a specific queue (not 'all') is selected
    if (!selectedQueueCode || selectedQueueCode === 'all') return;

    const currentStatusCode =
      updated?.current_status?.code ?? clearanceData?.current_status?.code ?? null;
    const queueName = getQueueNameForStatus(workflowStatuses as StatusMetadata, currentStatusCode);

    if (queueName && queueName !== selectedQueueCode) {
      const targetId = nextId || prevId || null;
      const nextParams = new URLSearchParams(searchParams);
      if (targetId) {
        nextParams.set('id', String(targetId));
        setSearchParams(nextParams, { replace: true });
      } else {
        nextParams.delete('id');
        setSearchParams(nextParams, { replace: true });
        onClose?.();
      }
    }
  };

  const { data: notesData } = useQuery({
    queryKey: [CLEARANCE_NOTES_QUERY_KEY, clearanceId],
    queryFn: () =>
      ConnectionsService.connectionsClearanceNotesList({
        id: clearanceId,
        ordering: '-created_at',
      }),
    enabled: !!clearanceId,
    select: (data: PaginatedClearanceNoteList | ClearanceNote) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if ('results' in data) return data.results || [];
      return [data as ClearanceNote];
    },
  });

  const { data: statusHistory } = useQuery({
    queryKey: [CLEARANCE_STATUS_HISTORY_QUERY_KEY, clearanceId],
    queryFn: () =>
      ConnectionsService.connectionsClearanceStatusHistoryRetrieve({
        id: clearanceId,
        sort: 'desc',
      }),
    enabled: !!clearanceId,
  });

  const { data: changeHistory } = useQuery({
    queryKey: [CLEARANCE_CHANGE_HISTORY_QUERY_KEY, clearanceId],
    queryFn: () =>
      ConnectionsService.connectionsClearanceChangeHistoryRetrieve({
        id: clearanceId,
        sort: 'desc',
      }),
    enabled: !!clearanceId,
  });

  const statusUpdateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      statusCode: string | null;
      reasonCode?: string | null;
      comment?: string;
      next_review_on?: string | null;
    }) => {
      const statusUpdateData: PatchedClearanceStatusUpdate = {
        code: data.statusCode || undefined,
        reason_code: data.reasonCode ?? null,
        comment: data.comment || null,
        next_review_on: data.next_review_on ?? undefined,
      };
      return ConnectionsService.connectionsClearanceStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: async (updatedClearance, variables) => {
      const { id } = variables;
      toast.success('Clearance status successfully updated');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_QUERY_KEY, id] }),
        queryClient.invalidateQueries({
          queryKey: [listQueryKeyBase ?? ORGANIZATION_CLEARANCE_LIST_QUERY_KEY],
        }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_CHANGE_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_NOTES_QUERY_KEY, id] }),
        // Invalidate queue notification badges for this scope and global menu badges
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.Clearance],
        }),
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);

      // If item moved out of current queue, navigate to next/prev or close
      checkAndNavigateIfRemoved(updatedClearance as ClearanceDetail);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update clearance status' });
    },
  });

  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
    next_review_on?: string | null;
  }) => {
    if (!clearanceId) {
      toast.error('Clearance ID not found');
      return;
    }

    await statusUpdateMutation.mutateAsync({
      id: clearanceId,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      comment: data.comment,
      next_review_on: data.next_review_on,
    });
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  if (isLoadingClearance) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading clearance details...</div>
      </div>
    );
  }

  if (clearanceError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading clearance details. Please try again.
        </div>
      </div>
    );
  }

  if (!clearanceData) {
    return null;
  }

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={clearanceData?.current_status?.code || null}
        targetStatusCode={pendingTargetStatus}
        workflowScope="connections.clearance"
        isPending={statusUpdateMutation.isPending}
        currentReasonCode={clearanceData?.current_reason?.code || null}
        currentStatusTitle={clearanceData?.current_status?.title || null}
      />
      <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-6">
        {/* Top Section - Provider Info, Status, Close */}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mb-4">
          {/* Provider Avatar - hidden on mobile */}
          <div className="tw:hidden tw:md:flex tw:items-center tw:justify-center tw:size-12 tw:shrink-0 tw:bg-brand-green-1 tw:text-white tw:rounded-full tw:md:order-1">
            <i className="fa-solid fa-user tw:text-lg" />
          </div>
          {/* Provider Name and View Profile link */}
          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:order-2">
            <span className="tw:text-base tw:md:text-xl tw:font-bold">
              {clearanceData.provider?.display_name}
            </span>
            {clearanceData.provider?.id && (
              <Link
                to={`/app/healthcare-entity/providers/${clearanceData.provider.id}`}
                target={isStaff ? '_blank' : undefined}
                rel={isStaff ? 'noopener noreferrer' : undefined}
                className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0 tw:w-fit"
              >
                View Profile
              </Link>
            )}
          </div>
          {/* Status Pill - always below provider info (left-aligned) */}
          <div className="tw:order-4 tw:w-full tw:flex tw:flex-col tw:items-start tw:gap-2">
            <StatusPill scope={WorkflowScopes.Clearance} status={clearanceData.current_status} />
          </div>
          {/* Close Button - right side of row 1 */}
          {onClose && <DrawerCloseButton onClick={onClose} className="tw:order-3" />}
        </div>

        {/* Green Section - DOB, NPI, Specialties */}
        <ClearanceQuickDetails clearance={clearanceData} />
        {canShowStatusTransitions && clearanceData?.reveal_name && (
          <>
            {/* Status Transition Buttons */}
            <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:mt-4">
              <StatusTransitionButtons
                isLoading={isLoadingClearance}
                currentStatusCode={clearanceData?.current_status?.code}
                workflowMetadata={workflowStatuses as StatusMetadata}
                onStatusClick={(status) => {
                  setPendingTargetStatus(status.code);
                  setIsStatusModalOpen(true);
                }}
                isPending={statusUpdateMutation.isPending}
                small={isMobile}
              />
            </div>
          </>
        )}

        {/* Details and Connections Tabs */}
        <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-4">
          <div className="tw:flex tw:flex-col tw:w-full">
            {isOverflowCollapsed ? (
              <div className="tw:pb-2">
                <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
                <DropdownSelect
                  inputId="clearance-drawer-section"
                  aria-label="Clearance section"
                  allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                  value={activeTab}
                  isSearchable={false}
                  isClearable={false}
                  onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
                    if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                  }}
                />
              </div>
            ) : null}
            <div
              aria-hidden={isOverflowCollapsed}
              ref={tabsMeasureRef}
              className={twMerge(
                'tw:w-full',
                isOverflowCollapsed &&
                  'tw:invisible tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'
              )}
            >
              <TabsList
                className={
                  isOverflowCollapsed
                    ? 'tw:border-transparent tw:dark:!border-transparent'
                    : undefined
                }
              >
                {tabs.map((t) => (
                  <TabsButton key={t.code} tab={t.code}>
                    {t.label}
                  </TabsButton>
                ))}
              </TabsList>
            </div>
          </div>

          <TabsContent tab="details">
            <ClearanceDetailsTab clearance={clearanceData} />
          </TabsContent>
          <TabsContent tab="connections">
            <ConnectionsTab clearance={clearanceData} />
          </TabsContent>
          <TabsContent tab="documents">
            <DocumentsTab
              contextType="connections.clearance"
              contextId={clearanceId}
              ownerId={clearanceData.healthcare_entity.id}
              relatedProviderId={clearanceData.provider?.id}
            />
          </TabsContent>
        </Tabs>

        <div className="tw:mt-2 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
          <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
          <ActivityLog
            notes={notesData}
            enableCommentsFilter={true}
            enableStatusChangesFilter={clearanceData.reveal_name}
            enableUpdatesFilter={clearanceData.reveal_name}
            enableEventsFilter={false}
            statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
            changeHistory={changeHistory as ActivityHistory[] | undefined}
            eventHistory={undefined}
            composerPlaceholder="Enter a comment..."
            noteMutations={{
              createNote: async (data) => {
                return ConnectionsService.connectionsClearanceNotesCreate({
                  id: clearanceId,
                  requestBody: {
                    clearance: clearanceId,
                    comment: data.comment,
                  } as ClearanceNote,
                });
              },
              updateNote: async (noteId: string, data: PatchedClearanceNote) => {
                return ConnectionsService.connectionsClearanceNotePartialUpdate({
                  id: noteId,
                  requestBody: data,
                });
              },
              deleteNote: async (noteId: string) => {
                return ConnectionsService.connectionsClearanceNoteDestroy({
                  id: noteId,
                });
              },
              notesQueryKey: [CLEARANCE_NOTES_QUERY_KEY, clearanceId],
              extraInvalidateQueryKeys: [[CLEARANCE_CHANGE_HISTORY_QUERY_KEY, clearanceId]],
            }}
          />
        </div>
      </div>
    </>
  );
}
